import React, { useState, useEffect } from "react";
import {
  CardHeader,
  CardContent,
  IconButton,
  List,
  Typography,
  Popper,
  ClickAwayListener,
  Tooltip,
} from "@material-ui/core";
import Skeleton from "@material-ui/lab/Skeleton";
import { Close, Visibility, VisibilityOff } from "@material-ui/icons";
import { Trans, useTranslation } from "react-i18next";
import ItemContentRevision from "../../list-item/ItemContentRevision";
import { contentClient } from "../../../AxiosClient";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import { BlockPicker } from "react-color";
import { Virtuoso } from "react-virtuoso";
import { AutoSizer } from "react-virtualized";
import SvgIcon from "@material-ui/core/SvgIcon";


const useStyles = makeStyles((theme) => ({
  "@global": {
    "del.diffmod": {
      backgroundColor: "#df9898",
    },
    "ins.diffmod": {
      backgroundColor: "#ddfade",
    },
    "ins.diffins": {
      backgroundColor: "#ddfade",
    },
    "del.diffdel": {
      backgroundColor: "#df9898",
    },
    "ins.mod": {
      backgroundColor: "#9fd9fb",
    },
  },
  swatch: {
    padding: 3,
    cursor: "pointer",
    boxShadow: "0 0 0 1px rgba(0,0,0,.1)",
    display: "inline-block",
  },
  color: {
    width: 12,
    height: 12,
  },
  popper: {
    zIndex: 99999999999,
    marginTop: 7,
  },
  publishedAvatar: {
    height: theme.spacing(5),
    width: theme.spacing(5),
    marginRight: theme.spacing(1),
    fontSize: "small",
    backgroundColor: theme.palette.success.main,
  },
  waitingAvatar: {
    height: theme.spacing(5),
    width: theme.spacing(5),
    marginRight: theme.spacing(1),
    fontSize: "small",
    backgroundColor: theme.palette.warning.main,
  },
  draftAvatar: {
    height: theme.spacing(5),
    width: theme.spacing(5),
    marginRight: theme.spacing(1),
    fontSize: "small",
    backgroundColor: theme.palette.info.main,
  },
  disabledAvatar: {
    height: theme.spacing(5),
    width: theme.spacing(5),
    marginRight: theme.spacing(1),
    fontSize: "small",
    backgroundColor: theme.palette.common.white,
  },
}));

function SidebarDiff(props) {
  const theme = useTheme();
  const { t } = useTranslation();
  const [revisions, setRevisions] = useState([]);
  const [insertionColour, setInsertionColour] = useState("#ddfade");
  const [deletionColour, setDeletionColour] = useState("#df9898");
  const [modificationColour, setModificationColour] = useState("#9fd9fb");
  const [showInsertionPicker, setShowInsertionPicker] = useState(false);
  const [showDeletionPicker, setShowDeletionPicker] = useState(false);
  const [showModificationPicker, setShowModificationPicker] = useState(false);
  const [insertionAnchorEl, setInsertionAnchorEl] = useState(null);
  const [deletionAnchorEl, setDeletionAnchorEl] = useState(null);
  const [modificationAnchorEl, setModificationAnchorEl] = useState(null);
  const [displayInsertions, setDisplayInsertions] = useState(true);
  const [displayDeletions, setDisplayDeletions] = useState(true);
  const [displayModifications, setDisplayModifications] = useState(true);
  const [page, setPage] = useState(0);
  const [isAtEnd, setIsAtEnd] = useState(false);
  const classes = useStyles();
  const colours = [
    "#4dac26",
    "#008837",
    "#018571",
    "#d7191c",
    "#d01c8b",
    "#7b3294",
    "#a6611a",
    "#2c7bb6",
    "#5ab4ac",
    "#c51b7d",
    "#e9a3c9",
    "#af8dc3",
    "#7fbf7b",
    "#ef8a62",
    "#fddbc7",
    "#de2d26",
    "#91cf60",
    "#ddfade",
    "#df9898",
    "#9fd9fb",
  ];
  const reviewer =
    "M20,2H4A2,2 0 0,0 2,4V16A2,2 0 0,0 4,18H8L12,22L16,18H20A2,2 0 0,0 22,16V4A2,2 0 0,0 20,2M12,4.3C13.5,4.3 14.7,5.5 14.7,7C14.7,8.5 13.5,9.7 12,9.7C10.5,9.7 9.3,8.5 9.3,7C9.3,5.5 10.5,4.3 12,4.3M18,15H6V14.1C6,12.1 10,11 12,11C14,11 18,12.1 18,14.1V15Z";
  const approver =
    "M21.1,12.5L22.5,13.91L15.97,20.5L12.5,17L13.9,15.59L15.97,17.67L21.1,12.5M10,17L13,20H3V18C3,15.79 6.58,14 11,14L12.89,14.11L10,17M11,4A4,4 0 0,1 15,8A4,4 0 0,1 11,12A4,4 0 0,1 7,8A4,4 0 0,1 11,4Z";
  const published =
    "M17,21L14.25,18L15.41,16.84L17,18.43L20.59,14.84L21.75,16.25M12.8,21H5C3.89,21 3,20.1 3,19V5C3,3.89 3.89,3 5,3H19A2,2 0 0,1 21,5V12.8C20.12,12.29 19.09,12 18,12L17,12.08V11H7V13H14.69C13.07,14.07 12,15.91 12,18C12,19.09 12.29,20.12 12.8,21M12,15H7V17H12M17,7H7V9H17";
  const waiting =
    "M6,2H18V8H18V8L14,12L18,16V16H18V22H6V16H6V16L10,12L6,8V8H6V2M16,16.5L12,12.5L8,16.5V20H16V16.5M12,11.5L16,7.5V4H8V7.5L12,11.5M10,6H14V6.75L12,8.75L10,6.75V6Z";
  const draft =
    "M8,12H16V14H8V12M10,20H6V4H13V9H18V12.1L20,10.1V8L14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H10V20M8,18H12.1L13,17.1V16H8V18M20.2,13C20.3,13 20.5,13.1 20.6,13.2L21.9,14.5C22.1,14.7 22.1,15.1 21.9,15.3L20.9,16.3L18.8,14.2L19.8,13.2C19.9,13.1 20,13 20.2,13M20.2,16.9L14.1,23H12V20.9L18.1,14.8L20.2,16.9Z";
  const editInProgress =
    "M14.06,9L15,9.94L5.92,19H5V18.08L14.06,9M17.66,3C17.41,3 17.15,3.1 16.96,3.29L15.13,5.12L18.88,8.87L20.71,7.04C21.1,6.65 21.1,6 20.71,5.63L18.37,3.29C18.17,3.09 17.92,3 17.66,3M14.06,6.19L3,17.25V21H6.75L17.81,9.94L14.06,6.19Z";
  const superseded =
    "M12,20A8,8 0 0,0 20,12A8,8 0 0,0 12,4A8,8 0 0,0 4,12A8,8 0 0,0 12,20M12,2A10,10 0 0,1 22,12A10,10 0 0,1 12,22C6.47,22 2,17.5 2,12A10,10 0 0,1 12,2M12.5,7V12.25L17,14.92L16.25,16.15L11,13V7H12.5Z";
  const removedFromDraft =
    "M19,6.41L17.59,5L12,10.59L6.41,5L5,6.41L10.59,12L5,17.59L6.41,19L12,13.41L17.59,19L19,17.59L13.41,12L19,6.41Z";

  useEffect(
    () => {
      setDisplayInsertions(true);
      setDisplayModifications(true);
      setDisplayDeletions(true);
      setInsertionColour("#ddfade");
      setDeletionColour("#df9898");
      setModificationColour("#9fd9fb");
      document
        .querySelectorAll("del.diffmod")
        .forEach((ele) => (ele.style["background-color"] = deletionColour));
      document
        .querySelectorAll("del.diffdel")
        .forEach((ele) => (ele.style["background-color"] = deletionColour));
      document
        .querySelectorAll("ins.diffmod")
        .forEach((ele) => (ele.style["background-color"] = insertionColour));
      document
        .querySelectorAll("ins.diffins")
        .forEach((ele) => (ele.style["background-color"] = insertionColour));
      document
        .querySelectorAll("ins.mod")
        .forEach((ele) => (ele.style["background-color"] = modificationColour));
    },
    [props.show]
  );

  useEffect(
    () => {
      document
        .querySelectorAll("del.diffmod")
        .forEach((ele) => (ele.style["background-color"] = deletionColour));
      document
        .querySelectorAll("del.diffdel")
        .forEach((ele) => (ele.style["background-color"] = deletionColour));
    },
    [deletionColour]
  );

  useEffect(
    () => {
      document
        .querySelectorAll("ins.diffmod")
        .forEach((ele) => (ele.style["background-color"] = insertionColour));
      document
        .querySelectorAll("ins.diffins")
        .forEach((ele) => (ele.style["background-color"] = insertionColour));
    },
    [insertionColour]
  );

  useEffect(
    () => {
      document
        .querySelectorAll("ins.mod")
        .forEach((ele) => (ele.style["background-color"] = modificationColour));
    },
    [modificationColour]
  );

  useEffect(
    () => {
      getRevisions()
    },
    [props.show,props.internalDocument]
  );
  const getRevisions = () => {
    !isAtEnd &&
    props.show &&
    props.internalDocument &&
      contentClient
        .get(
          `/knowledgeitem/${
            props.internalDocument.contentRVO.itemId
          }/revision/?page=${page}&size=20`
        )
        .then((res) => {
          if (res.data.last) {
            setIsAtEnd(true)
          } else {
            setPage(page + 1);
          }
          
          if (res.data.number === 0) {
            setRevisions(res.data.content);
          } else {
            setRevisions([...revisions, ...res.data.content]);
          }
        });
  }
  const onItemClick = (revision) => {
    props.onRevisionClick(revision);
  };

  const diffingLiveRevision = () => {
    if (!props.diffRevisionNumber) {
      return false;
    }
    let matchingRevision = revisions.filter(
      (r) => r.revisionNumber === props.diffRevisionNumber
    );
    return matchingRevision.length > 0 && matchingRevision[0].isLive;
  };

  const onColourClick = (type, event) => {
    if (type === "insertion") {
      setInsertionAnchorEl(insertionAnchorEl ? null : event.currentTarget);
      setShowInsertionPicker(!showInsertionPicker);
    } else if (type === "deletion") {
      setDeletionAnchorEl(deletionAnchorEl ? null : event.currentTarget);
      setShowDeletionPicker(!showDeletionPicker);
    } else if (type === "modification") {
      setModificationAnchorEl(
        modificationAnchorEl ? null : event.currentTarget
      );
      setShowModificationPicker(!showModificationPicker);
    }
  };

  const onColourSelected = (type, colour) => {
    if (type === "insertion") {
      setInsertionColour(colour.hex);
    } else if (type === "deletion") {
      setDeletionColour(colour.hex);
    } else if (type === "modification") {
      setModificationColour(colour.hex);
    }
  };

  const onClickAway = () => {
    setShowInsertionPicker(false);
    setShowModificationPicker(false);
    setShowDeletionPicker(false);
    setInsertionAnchorEl(null);
    setDeletionAnchorEl(null);
    setModificationAnchorEl(null);
  };

  const getPopper = (anchorElement, type) => {
    let colour;
    let open;
    if (type === "insertion") {
      colour = insertionColour;
      open = showInsertionPicker;
    } else if (type === "deletion") {
      colour = deletionColour;
      open = showDeletionPicker;
    } else if (type === "modification") {
      colour = modificationColour;
      open = showModificationPicker;
    }
    return (
      <Popper
        open={open}
        anchorEl={anchorElement}
        placement={"bottom"}
        className={classes.popper}
        modifiers={{
          flip: {
            enabled: true,
          },
          preventOverflow: {
            enabled: true,
            escapeWithReference: false,
          },
        }}
      >
        <BlockPicker
          id={type}
          color={colour}
          colors={colours}
          onChangeComplete={(colour) => onColourSelected(type, colour)}
        />
      </Popper>
    );
  };

  const toggleVisibility = (type) => {
    if (type === "insertion") {
      if (displayInsertions) {
        document.querySelectorAll("ins.diffmod").forEach((ele) => {
          ele.style["background-color"] = "transparent";
          ele.style["text-decoration"] = "none";
        });
        document.querySelectorAll("ins.diffins").forEach((ele) => {
          ele.style["background-color"] = "transparent";
          ele.style["text-decoration"] = "none";
        });
      } else {
        document.querySelectorAll("ins.diffmod").forEach((ele) => {
          ele.style["background-color"] = insertionColour;
          ele.style["text-decoration"] = "underline";
        });
        document.querySelectorAll("ins.diffins").forEach((ele) => {
          ele.style["background-color"] = insertionColour;
          ele.style["text-decoration"] = "underline";
        });
      }
      setDisplayInsertions(!displayInsertions);
    } else if (type === "deletion") {
      if (displayDeletions) {
        document.querySelectorAll("del.diffmod").forEach((ele) => {
          ele.style["background-color"] = "transparent";
          ele.style["text-decoration"] = "none";
        });
        document.querySelectorAll("del.diffdel").forEach((ele) => {
          ele.style["background-color"] = "transparent";
          ele.style["text-decoration"] = "none";
        });
      } else {
        document.querySelectorAll("del.diffmod").forEach((ele) => {
          ele.style["background-color"] = deletionColour;
          ele.style["text-decoration"] = "line-through";
        });
        document.querySelectorAll("del.diffdel").forEach((ele) => {
          ele.style["background-color"] = deletionColour;
          ele.style["text-decoration"] = "line-through";
        });
      }
      setDisplayDeletions(!displayDeletions);
    } else if (type === "modification") {
      if (displayModifications) {
        document.querySelectorAll("ins.mod").forEach((ele) => {
          ele.style["background-color"] = "transparent";
          ele.style["text-decoration"] = "none";
        });
      } else {
        document.querySelectorAll("ins.mod").forEach((ele) => {
          ele.style["background-color"] = modificationColour;
          ele.style["text-decoration"] = "underline";
        });
      }
      setDisplayModifications(!displayModifications);
    }
  };
  const revisionRowRenderer = (idx) => {
    const revision = revisions[idx];
    if (!revision) {
      return <div style={{ height: 1 }} />;
    }
    return props.internalDocument.contentRVO.revisionId ===
      revision.revisionId
      ? <></>
      :
      (
        <ItemContentRevision
          key={idx}
          discussionPortlet={false}
          onItemClick={() => onItemClick(revision)}
          onRevisionClick={() => onItemClick(revision)}
          data={revision}
          extendedSettings={JSON.stringify([
            {
              label:
                "settings_content_revision_created_date",
              order: 0,
            },
            {
              label: "settings_content_revision_author",
              order: 1,
            },
            {
              label: "settings_content_revision_rationale",
              order: 2,
            },
          ])}
          secondarySetting={"workflowState"}
        />

      )
  };
  return (
    <>
      {props.show && (

        

        <div style={{display: "flex", flexDirection: "column", height: "100%"}}>
          <CardHeader
            titleTypographyProps={{ component: "h3" }}
            title={t("diff_sidebar_title")}
            action={
              <IconButton
                onClick={props.toggleSidebar}
                aria-label={"Close sidebar"}
              >
                <Close />
              </IconButton>
            }
          />


          <CardContent style={{ height: "100%", paddingLeft: 12, paddingRight: 0 }}>
          <div style={{ height: "100%", display: "flex", flexDirection: "column"}}>
          {!!props.diffRevisionNumber ? (
              <>
                <div style={{ marginBottom: 30,  }}>
                  <Typography gutterBottom>
                    {diffingLiveRevision() ? (
                      <Trans
                        i18nKey="diff_sidebar_showing_differences_this_live"
                        values={{
                          revisionNumber: props.diffRevisionNumber,
                        }}
                        components={{ bold: <strong /> }}
                      />
                    ) : (
                      t("diff_sidebar_showing_differences", {
                        revisionNumber: props.diffRevisionNumber,
                      })
                    )}
                  </Typography>
                  <ClickAwayListener onClickAway={onClickAway}>
                    <div>
                      <Typography variant="h6" style={{ fontSize: "1rem" }}>
                        {t("diff_sidebar_key")}
                      </Typography>
                      <Typography variant="caption" gutterBottom>
                        {t("diff_sidebar_modify_colours")}
                      </Typography>
                      <div style={{ marginBottom: 5, marginTop: 10 }}>
                        <Tooltip title="Toggle highlighting">
                          <IconButton
                            size="small"
                            onClick={() => toggleVisibility("insertion")}
                          >
                            {displayInsertions ? (
                              <Visibility />
                            ) : (
                              <VisibilityOff />
                            )}
                          </IconButton>
                        </Tooltip>
                        <div
                          style={{
                            backgroundColor: insertionColour,
                            cursor: "pointer",
                            display: "inline",
                            textDecoration: "underline",
                          }}
                          onClick={(e) => onColourClick("insertion", e)}
                        >
                          {t("diff_sidebar_text_added")}
                        </div>
                      </div>
                      <div>
                        {showInsertionPicker &&
                          getPopper(insertionAnchorEl, "insertion")}
                      </div>
                      <div style={{ marginBottom: 5 }}>
                        <Tooltip title="Toggle highlighting">
                          <IconButton
                            size="small"
                            onClick={() => toggleVisibility("deletion")}
                          >
                            {displayDeletions ? (
                              <Visibility />
                            ) : (
                              <VisibilityOff />
                            )}
                          </IconButton>
                        </Tooltip>
                        <div
                          style={{
                            backgroundColor: deletionColour,
                            cursor: "pointer",
                            display: "inline",
                          }}
                          onClick={(e) => onColourClick("deletion", e)}
                        >
                          <s>{t("diff_sidebar_text_deleted")}</s>
                        </div>
                      </div>
                      <div>
                        {showDeletionPicker &&
                          getPopper(deletionAnchorEl, "deletion")}
                      </div>
                      <div style={{ marginBottom: 5 }}>
                        <Tooltip title="Toggle highlighting">
                          <IconButton
                            size="small"
                            onClick={() => toggleVisibility("modification")}
                          >
                            {displayModifications ? (
                              <Visibility />
                            ) : (
                              <VisibilityOff />
                            )}
                          </IconButton>
                        </Tooltip>
                        <div
                          style={{
                            backgroundColor: modificationColour,
                            cursor: "pointer",
                            display: "inline",
                          }}
                          onClick={(e) => onColourClick("modification", e)}
                        >
                          <u>{t("diff_sidebar_formatting_changed")}</u>
                        </div>
                      </div>
                      <div>
                        {showModificationPicker &&
                          getPopper(modificationAnchorEl, "modification")}
                      </div>
                    </div>
                  </ClickAwayListener>
                </div>
              </>
            ) : null}    
              <div>
                <Typography gutterBottom>
                  {!!props.diffRevisionNumber
                    ? t("diff_sidebar_select_different_revision")
                    : t("diff_sidebar_select_revision")}
                </Typography>
              </div>  
              <div style={{ flex: "1 1 auto" }}>
                <AutoSizer>
                  {({ height, width }) => (
                    <>

                      {!props.diffRecent && (
                        <>
                          <div>
                            {revisions.length > 0 ? (
                              <Virtuoso
                                overscan={10}
                                totalCount={revisions.length}
                                itemContent={(idx) => revisionRowRenderer(idx)}
                                defaultItemHeight={63}
                                endReached={(idx) => {
                                  getRevisions();
                                }}
                                style={{ width: width, minWidth: "273px", height: height, minHeight: "400px" }}
                              />
                            ) : (
                              <>
                                {Array.from(Array(6)).map(() => {
                                  return (
                                    <div style={{ marginTop: 4, marginBottom: 12 }}>
                                      <Skeleton
                                        variant="rect"
                                        style={{ width: "35%", marginBottom: 5 }}
                                        height={25}
                                        animation="wave"
                                      />
                                      <Skeleton
                                        variant="rect"
                                        style={{ width: "90%" }}
                                        height={25}
                                        animation="wave"
                                      />
                                    </div>
                                  );
                                })}
                              </>
                            )}
                          </div>
                        </>
                      )}
                    </>
                  )}
                </AutoSizer>
              </div>
            </div>
          </CardContent>
        </div>

      )}
    </>

  );
}

export default SidebarDiff;
